import React, { useEffect, useMemo, useRef, useState } from "react";
import { Field, Wrapper, SearchInput, Chip, Dropdown, Option } from "./styled";
import { HelperText, Label } from "components/Input/styled";
import Icon from "components/Icon/Icon";
import { XCircleIcon, CaretDownIcon } from "@phosphor-icons/react";
import { debounce } from "utils/helper";
import LoadingSpinner from "components/PageLoader/Spinner";

const MultiSelectSearch = ({
  id,
  label = "Label",
  options = [],
  value = [],
  onChange,
  placeholder = "Select value",
  size = "M",
  disabled = false,
  required,
  maxSelect,
  onSearch,
  loading = false,
  errorText,
  hasWarn
}) => {
  const containerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [labelMap, setLabelMap] = useState({});

  const getLabelsFromIds = (idArray = []) => {
    if (!Array.isArray(idArray)) return []; // add this
    return idArray.map((id) => {
      const opt = options.find((o) => o.value === id || o.id === id);
      return opt ? opt.label : labelMap[id] || id;
    });
  };

  useEffect(() => {
    if (options?.length) {
      setLabelMap((prev) => {
        const updated = { ...prev };
        options.forEach((opt) => {
          updated[opt.value] = opt.label;
        });
        return updated;
      });
    }
  }, [options]);

  // Toggle selection
  const toggleOption = (option) => {
    const exists = value.includes(option.value);
    if (exists) {
      onChange(value.filter((v) => v !== option.value));
    } else {
      onChange([...value, option.value]);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  // Debounced search
  const debouncedSearch = useMemo(() => {
    if (!onSearch) return null;
    return debounce(onSearch, 500);
  }, [onSearch]);

  const visibleChips = Array.isArray(value) ? value : [];

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    setOpen(true);

    if (debouncedSearch) {
      debouncedSearch(val);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Backspace") {
      if (search === "" && value.length > 0) {
        // remove last selected option
        const lastValue = value[value.length - 1];
        onChange(value.filter((v) => v !== lastValue));
      } else if (search === "" && value.length === 0) {
        // trigger API when input empty
        if (debouncedSearch) debouncedSearch(""); // fetch all / reset
      }
    }
  };

  const visibleLabels = getLabelsFromIds(visibleChips, options);

  return (
    <Wrapper ref={containerRef}>
      {label && (
        <Label htmlFor={id} size={size}>
          {label}
          {required && <span>*</span>}
        </Label>
      )}

      <Field
        onClick={() => {
          if (!disabled) setOpen((prev) => !prev);
        }}
        size={size}
        disabled={disabled}
      >
        <div className="field-wrapper">
          {visibleLabels.map((label, idx) => (
            <Chip key={visibleChips[idx]}>
              <h5>{label}</h5>
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  toggleOption({ value: visibleChips[idx] });
                }}
              >
                <Icon component={XCircleIcon} size={size === "S" ? 10 : 12} />
              </span>
            </Chip>
          ))}

          {/* {hiddenCount > 0 && <Chip>+{hiddenCount}</Chip>} */}

          {/* Search input */}
          <SearchInput
            id={id}
            size={size}
            disabled={disabled}
            value={search}
            placeholder={placeholder}
            onChange={handleSearchChange}
            onKeyDown={handleKeyDown}
          />

          {/* Dropdown options */}
          {open && (
            <Dropdown size={size}>
              {loading ? (
                <Option size={size} disabled>
                  <LoadingSpinner
                    color="#797979"
                    text="Loading..."
                    fontSize={14}
                    size={16}
                    className="loading-spinner"
                  />
                </Option>
              ) : options.length === 0 ? (
                <Option size={size} disabled>
                  No results found
                </Option>
              ) : (
                options.map((option) => {
                  if (option?.isGroup) {
                    return (
                      <Option
                        key={option.value}
                        size={size}
                        className="toggle_label"
                      >
                        {option.label}
                      </Option>
                    );
                  }

                  const isOptionDisabled =
                    option.disabled ||
                    (maxSelect &&
                      !value.includes(option.value) &&
                      value.length >= maxSelect);

                  const isSelected = value.includes(option.value);

                  return (
                    <Option
                      key={option.value}
                      size={size}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isOptionDisabled) return;
                        toggleOption(option);
                        setSearch("");
                        // if (debouncedSearch) debouncedSearch("");
                      }}
                      className="select-checkbox"
                      selected={isSelected}
                      style={{
                        cursor: isOptionDisabled ? "not-allowed" : "pointer",
                        opacity: isOptionDisabled ? 0.5 : 1,
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={isOptionDisabled}
                        readOnly
                      />
                      {option.label}
                    </Option>
                  );
                })
              )}
            </Dropdown>
          )}
        </div>

        <Icon component={CaretDownIcon} color="#A5A5A5" size={14} />
      </Field>

      {(errorText || hasWarn) && (
        <HelperText size={size} hasError={errorText} hasWarn={hasWarn}>
          {errorText}
          {hasWarn}
        </HelperText>
      )}
    </Wrapper>
  );
};

export default MultiSelectSearch;
